import useLimits from '@/hooks/useLimits';
import {
  addUploadingFilesAtom,
  updateUploadingFileAtom,
} from '@/store/fileUploadStore';
import { useSetAtom } from 'jotai';
import { toast } from 'sonner';
import {
  FileUploadToastContent,
  UPLOAD_TOAST_ID,
} from './FileUploadToastContent';
import useIngestCustomAudio from './useIngestCustomAudio';
import useIngestCustomVideo from './useIngestCustomVideo';
import useIngestFile from './useIngestFile';

// Supported file extensions
export const SUPPORTED_EXTENSIONS = [
  // Documents
  '.pdf',
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.md',
  '.txt',
  '.epub',
  // Images
  '.png',
  '.jpg',
  '.jpeg',
  '.tiff',
  '.bmp',
  '.heic',
  // Video
  '.mp4',
  // Audio
  '.mp3',
  '.wav',
];

const useIngestMultipleFiles = () => {
  const { ingestFile } = useIngestFile();
  const { ingestCustomVideo } = useIngestCustomVideo();
  const { ingestCustomAudio } = useIngestCustomAudio();
  const { exceededLimits } = useLimits();
  const addFilesToUpload = useSetAtom(addUploadingFilesAtom);
  const updateFileStatus = useSetAtom(updateUploadingFileAtom);

  const ingestMultipleFiles = async (acceptedFiles: File[]) => {
    const files = acceptedFiles;

    if (exceededLimits?.isImportLimitExceeded && files.length > 0) {
      toast.error('Import limit exceeded.');
      return;
    }

    const filesToAdd = files.map((file) => ({ name: file.name }));
    const addedFilesState = addFilesToUpload(filesToAdd);

    toast.custom((t) => <FileUploadToastContent toastId={t} />, {
      id: UPLOAD_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      dismissible: false,
      position: 'bottom-left',
      className: '!px-0 !py-0 bg-popover border-border !rounded-xl',
    });

    const filesToArray = Array.from(files);

    const filesPromises = filesToArray.map(async (file, index) => {
      const fileState = addedFilesState[index];
      if (!fileState) {
        console.error('Mismatch between input files and state files');
        return null; // Should not happen
      }

      try {
        let chatbotId: string | null | undefined = null;

        if (file.type === 'video/mp4') {
          chatbotId = await ingestCustomVideo([file], false);
        } else if (file.type.startsWith('audio/')) {
          chatbotId = await ingestCustomAudio([file], false);
        } else {
          chatbotId = await ingestFile(file);
        }

        if (chatbotId) {
          updateFileStatus({
            id: fileState.id,
            status: 'IN_PROGRESS',
            chatbotId: chatbotId,
          });
          return chatbotId;
        }

        updateFileStatus({
          id: fileState.id,
          status: 'FAILED',
          errorMessage: 'File could not be processed.',
        });
        return null;
      } catch (error: unknown) {
        console.error('Error ingesting file:', file.name, error);
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';
        updateFileStatus({
          id: fileState.id,
          status: 'FAILED',
          errorMessage: errorMessage,
        });
        return null;
      }
    });

    Promise.race(filesPromises.map((p) => p.catch(() => null))).catch(
      () => null,
    );
  };

  const isFileEligible = (file: File): boolean => {
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    return SUPPORTED_EXTENSIONS.includes(extension);
  };

  const processFiles = (allFiles: File[]) => {
    const validFiles = allFiles.filter(isFileEligible);

    if (validFiles.length === 0) {
      toast.error('No supported files found');
      return;
    }

    if (validFiles.length < allFiles.length) {
      toast.warning(
        `${allFiles.length - validFiles.length} unsupported files were skipped`,
      );
    }

    handleFilesUpload(validFiles);
  };

  const handleFilesUpload = (files: File[]) => {
    if (files.length === 0) {
      toast.error('Please add a supported file.');
      return;
    }
    ingestMultipleFiles(files);
  };

  return { processFiles };
};

export default useIngestMultipleFiles;
